/**
 * The beta-test checklist (requested 2026-10-05): one real flight, in the order
 * a pilot meets things, for each setup VACARS5 supports.
 *
 * Plain data on purpose. The Beta test page renders it, the report carries
 * each item's id with its result, and the server and the admin page look items
 * up here by id — so an id, once used in a report, must never be reused for a
 * different check. Retire it and add a new one.
 *
 * `only` limits an item to setups carrying every listed tag. `hint` adds a line
 * for one tag where the sims behave differently (X-Plane sends no aircraft
 * title, has no touchdown-rate variable, and reads IN through datarefs nobody
 * has yet seen running).
 *
 * Text may contain <code>…</code> and nothing else: the page turns that one tag
 * into an element and shows everything else as plain text.
 */

export const CHECKLIST_VERSION = 3;   // 3: 2026-10-07, go-around check names the takeoff; 2: landing rate, cruise and arrival checks reworded

export const SETUPS = {
  "win-msfs":   { label: "Windows · MSFS",     tags: ["windows", "msfs"] },
  "win-xplane": { label: "Windows · X-Plane",  tags: ["windows", "xplane"] },
  "mac-xplane": { label: "Mac · X-Plane",      tags: ["mac", "xplane"] },
};

export const PHASES = [
  { id: "install", name: "Install and start", when: "Before you load the sim.", items: [
    { id: "mac-open", only: ["mac"], t: "First launch on a Mac",
      do: "Open VACARS5 for the first time after installing it.",
      exp: "macOS blocks it once. System Settings → Privacy & Security → Open Anyway lets it start, and it opens normally after that.",
      bad: "Note the exact message macOS showed and whether Open Anyway appeared." },
    { id: "update-win", only: ["windows"], t: "Update prompt",
      do: "When a newer version is published, start VACARS5 and wait a minute or two.",
      exp: "“VACARS5 x.y.z is ready to install” with Restart Now and Later. Restart Now relaunches it and the sidebar shows the new version.",
      bad: "Skip this if no update was published during the test. If one was and no prompt came, note it." },
    { id: "update-mac", only: ["mac"], t: "Update prompt",
      do: "When a newer version is published, start VACARS5.",
      exp: "“VACARS5 x.y.z is available” with Download, Later and Skip This Version. Download opens the right file for your Mac (Apple silicon or Intel).",
      bad: "Skip this if no update was published during the test. If one was and nothing appeared, note it." },
    { id: "xp-network", only: ["xplane"], t: "X-Plane accepts VACARS5",
      do: "In X-Plane, Settings → Network: turn on “Accept incoming connections”. Load the aircraft at a gate.",
      exp: "Live Tracking shows Connected · X-Plane within about 10 seconds.",
      bad: "Note the message under the connection status, and your X-Plane version (11 or 12)." },
    { id: "connect", t: "Sim connects",
      do: "Load the aircraft at the gate.",
      exp: "Live Tracking shows the sim as connected.",
      bad: "Press Reconnect once and note whether that fixed it.",
      hint: { msfs: "The log has <code>SimConnect opened with protocol=…</code>." } },
  ]},
  { id: "plan", name: "Planning", when: "Bid, flight plan and init.", items: [
    { id: "bid-appears", t: "A new bid appears without restarting",
      do: "Bid on a flight on FlyVAC with VACARS5 open, then switch back to VACARS5.",
      exp: "The bid is on the Dashboard within seconds, or after pressing Refresh.",
      bad: "Note whether Refresh brought it in." },
    { id: "bid-matches", t: "Dashboard row matches the bid",
      do: "Compare the Dashboard row with FlyVAC's bid confirmation page.",
      exp: "Date, Dep (L), Arr (L) and Fin · Reg all match.",
      bad: "Write both versions of whatever differs." },
    { id: "bid-delete", opt: true, t: "Delete a bid",
      do: "Bid a spare flight, then delete it with the bin icon on the Dashboard and confirm.",
      exp: "Gone from the Dashboard and from Flight Ops on FlyVAC.",
      bad: "Note where it is still listed." },
    { id: "ofp", t: "SimBrief plan loads",
      do: "Generate the OFP for this flight on SimBrief, then open Flight Plan (Reload if needed).",
      exp: "The plan for this route shows. If SimBrief's passenger count differs from your assignment, an amber note says so.",
      bad: "Note what the page said instead." },
    { id: "init-tail", t: "Init picks the assigned aircraft",
      do: "Open Init Flight for the bid.",
      exp: "The registration is already set to the bid's aircraft, with no amber “not in the fleet” note.",
      bad: "Note the bid's registration and the one selected." },
  ]},
  { id: "gate", name: "At the gate", when: "Initialised, parked, brake set.", items: [
    { id: "values", t: "Values match the instruments",
      do: "Compare Live Tracking with the cockpit: altitude, airspeed, ground speed, heading, vertical speed, fuel.",
      exp: "Each matches. Fuel is in kilograms.",
      bad: "Fuel about 2.2 times too high means pounds. Note any value that looks like another field's." },
    { id: "aircraft-name", only: ["msfs"], t: "Aircraft recognised",
      do: "Look for an aircraft-mismatch warning on Live Tracking.",
      exp: "None for the aircraft you booked. The log has <code>Aircraft: …</code>.",
      bad: "Note the aircraft name the warning shows." },
    { id: "sound-nav", t: "Announcements survive page changes",
      do: "When the boarding announcement starts, open Settings and come back.",
      exp: "It plays through without stopping.",
      bad: "Note when it stopped. Skip if your operator has no boarding clip." },
    { id: "soundboard", t: "Soundboard survives page changes",
      do: "On the Audio page play a clip by hand, then open the Dashboard.",
      exp: "It keeps playing. Back on Audio, its button still shows as playing.",
      bad: "Note whether it stopped or the button reset." },
  ]},
  { id: "taxi-out", name: "Pushback and taxi", when: "Pushback to the hold-short line.", items: [
    { id: "push-phase", t: "Pushback is TAXI OUT, never CLIMB",
      do: "Push back with Live Tracking open, and reopen the page once during the push.",
      exp: "The event log shows TAXI OUT and no CLIMB.",
      bad: "Note the phase it showed and when." },
    { id: "log-persists", t: "Event log keeps its times",
      do: "Open another page and return to Live Tracking.",
      exp: "The same entries with the same times (Z) as before.",
      bad: "Note whether entries vanished or changed time." },
    { id: "hold-short", t: "Hold short does not end the flight",
      do: "At the hold-short line, set the parking brake with the engines running.",
      exp: "Nothing happens: no IN, no filing.",
      bad: "If the flight filed, note the time." },
  ]},
  { id: "climb", name: "Takeoff and climb", when: "Rotation to top of climb.", items: [
    { id: "off-climb", t: "Takeoff is logged",
      do: "Take off.", exp: "The event log shows CLIMB at about the takeoff time.",
      bad: "Note the phase shown instead." },
    { id: "low-leveloff", opt: true, t: "Low level-off stays CLIMB",
      do: "If your departure levels off below 5,000 ft, watch the phase.",
      exp: "It stays CLIMB, and no approach announcement plays.",
      bad: "Note the altitude and the phase." },
    { id: "cruise-sound", t: "Cruise announcement at top of climb",
      do: "Note when the in-flight announcement plays.",
      exp: "Once, on reaching the flight plan's cruise level, not at a level-off on the way up.",
      bad: "Note the altitude it played at, your planned cruise level, and whether you had a SimBrief plan loaded." },
    { id: "overlay", t: "In-sim overlay updates",
      do: "Watch the overlay banner during the climb.",
      exp: "Progress, altitude and speed update; gates show when known.",
      bad: "Note what stopped updating." },
  ]},
  { id: "cruise", name: "Cruise", when: "Level at cruise.", items: [
    { id: "route-nav", t: "Route stays on the map",
      do: "Leave Live Tracking and return.", exp: "The route drawn so far is still there.",
      bad: "Note whether it was blank or partial." },
    { id: "route-restart", t: "Route survives a restart",
      do: "Quit VACARS5 from its tray or menu-bar icon and start it again.",
      exp: "The flight carries on and the whole route since the gate reappears.",
      bad: "Note how much of the route came back." },
    { id: "livemap", t: "Live Map shows you",
      do: "Open Live Map.", exp: "Your flight, with your pilot ID and name, and both airports marked.",
      bad: "Note what is missing." },
    { id: "hard-kill", opt: true, t: "A hard quit loses nothing",
      do: "Force-quit VACARS5 (Task Manager on Windows, Force Quit on a Mac), then start it again.",
      exp: "The log has <code>telemetry outbox: resuming flight … with N unsent frames</code>, and the route has no gap.",
      bad: "Note whether that line appeared, and the size of any gap." },
    { id: "net-outage", opt: true, t: "A network outage loses nothing",
      do: "Turn off Wi-Fi or unplug for 2–3 minutes while flying, then reconnect.",
      exp: "The route has no gap afterwards.", bad: "Note how long the gap was." },
  ]},
  { id: "descent", name: "Descent and landing", when: "Top of descent to touchdown.", items: [
    { id: "descent-sound", t: "Descent announcement once",
      do: "Start down.", exp: "One descent announcement, and the phase reads DESCENT.",
      bad: "Note any repeat or wrong clip." },
    { id: "descent-leveloff", t: "A level-off stays DESCENT",
      do: "Level off on the way down (a crossing restriction, or 10,000 ft for 250 kt).",
      exp: "The phase stays DESCENT and no cruise announcement plays.",
      bad: "Note the altitude and what played." },
    { id: "approach-sound", t: "Approach below 5,000 ft",
      do: "Descend through 5,000 ft.", exp: "The phase reads APPROACH and the approach clip plays once.",
      bad: "Note the altitude it changed at." },
    { id: "burst", t: "Landing burst",
      do: "Land, then find the line in the log written about 10 seconds after touchdown.",
      exp: "<code>landing burst closed (landed): N frames, vs at touchdown X fpm, … landing rate Y fpm (source)</code>, with no stutter on short final.",
      bad: "Paste the line. Note any stutter on short final.",
      hint: { msfs: "The source in brackets should be <code>touchdown_var</code>.",
              xplane: "The source in brackets should be <code>burst_min</code> (X-Plane has no touchdown-rate variable)." } },
    { id: "ldg-rate", t: "Landing rate matches",
      do: "Compare the landing rate on the overlay, on Live Tracking (about 10 s after touchdown) and in your logbook with the sim's or aircraft's touchdown rate.",
      exp: "All three show the same number, within a few fpm of the sim's.", bad: "Write all the numbers, and when each appeared." },
    { id: "go-around", opt: true, t: "Go-around",
      do: "Go around from below 100 ft, then land.",
      exp: "The log has <code>landing burst closed (go_around)</code> at the go-around (and not at your takeoff), and the approach announcement does not repeat.",
      bad: "Note what the log shows." },
  ]},
  { id: "taxi-in", name: "Taxi in and parking", when: "Runway exit to engines off.", items: [
    { id: "arrival", t: "Arrival announcement and reminder",
      do: "Taxi in.",
      exp: "The arrival clip plays once you have slowed to taxi speed (not on the landing roll); Live Tracking says to set the parking brake and shut down the engines.",
      bad: "Note what was missing." },
    { id: "in-brake", t: "IN on brake set and engines off",
      do: "At the gate, set the parking brake and shut down all engines.",
      exp: "IN logs within seconds, the PIREP files by itself, and the flight leaves the Dashboard.",
      bad: "Note what happened, and whether filing by hand worked.",
      hint: { xplane: "If IN only came about 30 seconds after you stopped, VACARS5 could not read the parking brake or engines from X-Plane. Note which aircraft." } },
  ]},
  { id: "after", name: "After filing", when: "On FlyVAC.", items: [
    { id: "logbook", t: "Logbook row is right",
      do: "Open the flight in your FlyVAC logbook.",
      exp: "Fin and registration match the bid, passengers match the assignment, and the times look right.",
      bad: "Write what each wrong field shows." },
    { id: "kml", t: "KML route downloads",
      do: "Click the KML icon on the flight's logbook row and open it in Google Earth.",
      exp: "The route from gate to gate, with altitude.",
      bad: "Note whether the file was empty." },
  ]},
];

/** The setup's tags, or none for an unknown setup. */
export function tagsFor(setup) {
  return SETUPS[setup]?.tags || [];
}

/** The phases and items that apply to `setup`, numbered for display. */
export function checklistFor(setup) {
  const tags = tagsFor(setup);
  return PHASES
    .map((p) => ({
      ...p,
      items: p.items
        .filter((it) => (it.only || []).every((tag) => tags.includes(tag)))
        .map((it) => ({ ...it, hints: Object.entries(it.hint || {}).filter(([tag]) => tags.includes(tag)).map(([, h]) => h) })),
    }))
    .filter((p) => p.items.length);
}

/** Pass / fail / skip counts over the items that apply to `setup`. */
export function summarise(setup, results = {}) {
  const items = checklistFor(setup).flatMap((p) => p.items);
  const c = { total: items.length, pass: 0, fail: 0, skip: 0, open: 0 };
  for (const it of items) {
    const s = results[it.id]?.status;
    if (s === "pass" || s === "fail" || s === "skip") c[s] += 1;
    else c.open += 1;
  }
  return c;
}

/**
 * The setup VACARS5 can see: the platform from the desktop app, and the sim
 * from the connection (simconnect and fsuipc are both MSFS). Null when either
 * is unknown, so the page asks instead of guessing.
 */
export function detectSetup(platform, sim) {
  const os = platform === "win32" ? "win" : platform === "darwin" ? "mac" : null;
  const s = sim === "simconnect" || sim === "fsuipc" ? "msfs" : sim === "xplane" ? "xplane" : null;
  const key = os && s ? `${os}-${s}` : null;
  return key && SETUPS[key] ? key : null;
}
